import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useFeesContext } from 'sonata-platform-components';
import { Tooltip, TooltipInteractionEnum } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { userDataFeesDictionary } from 'sonata-static-storage/lib/samples/userDataFees.samples';
import {
  DataFeeSectionEnum,
  SimplifiedPricingModelEnum
} from 'sonata-static-storage/lib/schemas/user/userDataFee.schema';

import './feesBar.styles.scss';

import type { CampaignDataFees } from 'sonata-static-storage/lib/common/common.types';
import type { UserDataFeeDictionarySubsection } from 'sonata-static-storage/lib/samples/userDataFees.samples';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type {
  DataFeeInterface,
  DataFeeSubsectionInterface,
  PriceInterface
} from 'sonata-static-storage/lib/schemas/user/userDataFee.schema';

// import { userDataFeesDictionary } from 'sonata-static-storage/lib/samples/userDataFees.samples';

type Props = {
  currencyInfo: BaseCurrencyInterface;
  pricingModel: SimplifiedPricingModelEnum;
};

function getFeeCostFormattedAmount({
  currencyInfo,
  feePrice,
  pricingModel
}: {
  currencyInfo: BaseCurrencyInterface;
  feePrice?: PriceInterface;
  pricingModel: SimplifiedPricingModelEnum;
}): string {
  const feeCost = feePrice ? feePrice[pricingModel] : 0;
  return normalizeWithUnit(feeCost, currencyInfo, 2);
}

export function FeesBar({ currencyInfo, pricingModel }: Props) {
  const {
    detailFees: { legacyFees },
    totalFees
  } = useFeesContext();
  const showInfoIcon = legacyFees.enabled;
  const { formatMessage } = useIntl();

  const subsectionFeeGroup = (
    subsection: UserDataFeeDictionarySubsection,
    subsectionFee: DataFeeSubsectionInterface
  ) => {
    const subsectionFeeCost = `${getFeeCostFormattedAmount({
      currencyInfo,
      feePrice: subsectionFee.price,
      pricingModel
    })} ${formatMessage({
      defaultMessage: pricingModel.toUpperCase(),
      id: `fees.pricingModel.${pricingModel}`
    })}`;
    const maxCostEnabled = !!subsectionFee.maxPrice;
    if (subsectionFee.options?.length) {
      return (
        <>
          <div className="fees-tooltip__detail-group">
            <p className="fees-tooltip__detail">
              {subsection?.copy}:{subsectionFeeCost}
              {maxCostEnabled
                ? formatMessage({
                    defaultMessage: '(Max cost)',
                    id: 'fees.subsection.maxCost'
                  })
                : ''}
            </p>
          </div>
          <div className="fees-tooltip__detail-group">
            <ul className="fees-tooltip__detail-list">
              {subsectionFee.options.map(optionFee => {
                const optionFeeCost = getFeeCostFormattedAmount({
                  currencyInfo,
                  feePrice: optionFee.price,
                  pricingModel
                });
                const option = subsection.options?.find(o => o.option === optionFee.option);
                return (
                  <li className="fees-tooltip__detail-list-item" key={optionFee.option}>
                    {option?.copy || 'N/A'}: {optionFeeCost}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      );
    } else {
      return (
        <div className="fees-tooltip__detail-group">
          <p className="fees-tooltip__detail">
            {subsection?.copy}:<span> </span>
            {subsectionFeeCost}
            {maxCostEnabled
              ? formatMessage({
                  defaultMessage: '(Max cost)',
                  id: 'fees.subsection.maxCost'
                })
              : ''}
          </p>
        </div>
      );
    }
  };

  const sectionFeeHeader = (sectionFee?: DataFeeInterface) => {
    const sectionFeeCost = getFeeCostFormattedAmount({
      currencyInfo,
      feePrice: sectionFee?.price,
      pricingModel
    });
    const pricingModelCopy = formatMessage({
      defaultMessage: pricingModel.toUpperCase(),
      id: `fees.pricingModel.${pricingModel}`
    });
    const maxCostEnabled = sectionFee?.subsections?.some(
      subsection => subsection.enabled && !subsection.allIncludedPrice && subsection.maxPrice
    );
    const allIncludedPrice = sectionFee?.subsections.some(
      subsection => subsection.enabled && !!subsection.allIncludedPrice
    );
    const maxCostContent = () => {
      if (!maxCostEnabled) {
        return null;
      }
      return (
        <>
          <span> </span>
          {formatMessage({
            defaultMessage: '(Max cost)',
            id: 'fees.subsection.maxCost'
          })}
        </>
      );
    };
    const allIncludedPriceContent = () => {
      if (!allIncludedPrice) {
        return null;
      }
      return (
        <>
          <span> </span>
          {formatMessage({
            defaultMessage: '(All included price)',
            id: 'fees.subsection.allIncludedPrice'
          })}
        </>
      );
    };
    return (
      <div className="fees-tooltip__price">
        {sectionFeeCost}
        <span> </span>
        {pricingModelCopy}
        {maxCostContent()}
        {allIncludedPriceContent()}
      </div>
    );
  };

  const feesInfo = useCallback(() => {
    return (
      <div className="fees-tooltip">
        <div className="fees-tooltip__header">
          <span className="fees-tooltip__header-col fees-tooltip__header-col--type">Type</span>
          <span className="fees-tooltip__header-col fees-tooltip__header-col--fees">Campaign Data Fees</span>
        </div>
        {(legacyFees as CampaignDataFees)?.sections?.map(sectionFee => {
          const section = userDataFeesDictionary.find(({ section }) => section === sectionFee.section);
          const sectionFeeCopy =
            sectionFee?.section === DataFeeSectionEnum.audience ? 'Audience' : section?.copy || 'N/A';
          if (!section && sectionFee.section !== DataFeeSectionEnum.audience && !sectionFee) {
            return null;
          }
          let subsections = sectionFee.subsections;
          if (sectionFee.section === DataFeeSectionEnum.audience) {
            subsections = [];
          }
          return (
            <div className="fees-tooltip__row" key={`fee_${section?.section}`}>
              <span className="fees-tooltip__type">{sectionFeeCopy}</span>
              <div className="fees-tooltip__prices">
                {sectionFeeHeader(sectionFee)}
                {subsections.map(subsectionFee => {
                  const subsection = section?.subsections.find(s => s.subsection === subsectionFee.subsection);
                  return (
                    <div className="fees-tooltip__details" key={`subsection_${subsection?.subsection}`}>
                      {subsectionFeeGroup(subsection!, subsectionFee)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [legacyFees]);

  if (totalFees === 0) {
    return null;
  }

  return (
    <aside aria-live="polite" className="fees-bar" role="note">
      <div className="fees-bar__content">
        <p className="fees-bar__message">
          Be aware that for the selected settings and segmentation there is an
          <span className="fees-bar__highlight"> additional Data Fee </span>
          applied of {normalizeWithUnit(totalFees, currencyInfo, 2)}.
        </p>
        {showInfoIcon && (
          <Tooltip
            config={{
              arrow: true,
              component: feesInfo(),
              forcePlacement: false,
              interactions: [TooltipInteractionEnum.hover],
              padding: '0',
              placement: 'right',
              portal: true,
              strategy: 'fixed'
            }}
          >
            <button aria-label="More information about the data fee" className="fees-bar__info-button" type="button">
              <i className="fa fa-info-circle" />
            </button>
          </Tooltip>
        )}
      </div>
    </aside>
  );
}
